import React from 'react';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { Text, TextVariant } from '@amzn/stencil-react-components/text';
import { Expander, ExpanderLayout } from '@amzn/stencil-react-components/expander';
import { useTranslation } from "react-i18next";
import Footer from 'src/components/common/Footer';
import ScreenNames from 'src/constants/screen-names';
import useRecordScreenVisit from 'src/hooks/useRecordScreenVisit';
import useHashExpander from 'src/hooks/useHashExpander';
import iosDeviceIcon from 'src/images/ios_device_icon.svg';
import androidDeviceIcon from 'src/images/android_device_icon.svg';
import macDeviceIcon from 'src/images/mac_device_icon.svg';
import windowsDeviceIcon from 'src/images/windows_device_icon.svg';


/**
 * PasskeyHelp component provides information and assistance about passkeys
 */
const PasskeyHelp: React.FC = () => {
    const { t } = useTranslation();
    const { isExpanded, toggleSection } = useHashExpander();

    // Record screen visit for analytics
    useRecordScreenVisit(ScreenNames.PASSKEY_HELP);

    // Helper function to render phone links
    const renderPhoneLinks = (phoneString: string) => {
        if (!phoneString) return null;
        // Split by "or" or commas
        const phones = phoneString.split(/\s+or\s+|\s*,\s*/);
        return phones.map((phone: string, index: number) => {
            // Handle special cases like "(Manila)" or "(Cebu)"
            const match = phone.match(/(.*?)(\s*\([^)]+\)\s*)$/);
            let displayPhone = phone;
            let label = '';
            if (match) {
                displayPhone = match[1].trim();
                label = match[2];
            }
            // Clean the phone number for the href (remove spaces, parentheses, etc.)
            const cleanPhone = displayPhone.replace(/\s+|\(|\)|-/g, '');
            return (
                <React.Fragment key={index}>
                    {index > 0 && ' or '}
                    <a href={`tel:${cleanPhone}`} style={{ color: token('color.link.primary-default'), textDecoration: 'underline' }}>
                        {displayPhone}
                    </a>
                    {label}
                </React.Fragment>
            );
        });
    };

    return (
        <>
            <Col className='centered-content' data-testid='passkey-help-content'
                style={{ zIndex: 0, maxWidth: '800px', margin: '0 auto' }}>
                <View padding={{ top: 16, bottom: 20, left: 16, right: 16 }} backgroundColor="color.surface.bg-default">
                    <Col gridGap="S300">
                        {/* Title */}
                        <View
                            width="600px"
                            height="72px"
                            padding="S300"
                            style={{ display: 'flex', flexDirection: 'row' }}
                        >
                            <Text fontSize="T400" fontWeight="bold"
                                color="color.text.primary">{t('passkey_help_title')}</Text>
                        </View>

                        {/* Section 1: What is Passkey? */}
                        <Expander
                            isExpanded={isExpanded('what-is-passkey')}
                            onToggle={() => toggleSection('what-is-passkey')}
                            titleText={t('passkey_help_section_1_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({ titleText }) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="color.text.primary"
                                    style={{ cursor: 'pointer', height: '24px' }}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col id="what-is-passkey" gridGap="S400">
                                <Text color="color.text.primary">{t('passkey_help_section_1_content')}</Text>
                                <div>
                                    <Text color="color.text.primary">{t('passkey_help_section_1_benefits')}</Text>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_1_bullet_1')}</Text></li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_1_bullet_2')}</Text></li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_1_bullet_3')}</Text></li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_1_bullet_4')}</Text></li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_1_bullet_5')}</Text></li>
                                    </ul>
                                </div>
                            </Col>
                        </Expander>

                        {/* Section 2: How do I set up a Passkey? */}
                        <Expander
                            isExpanded={isExpanded('setup')}
                            onToggle={() => toggleSection('setup')}
                            titleText={t('passkey_help_section_2_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({ titleText }) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="color.text.primary"
                                    style={{ cursor: 'pointer', height: '24px' }}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col id="setup" gridGap="S400">
                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_section_2_requirements_title')}</Text>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_requirement_1')}</Text>
                                        </li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_requirement_2')}</Text>
                                        </li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_requirement_3')}</Text>
                                        </li>
                                    </ul>
                                </Col>

                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_section_2_setup_title')}</Text>
                                    <Text color="color.text.primary">{t('passkey_help_section_2_setup_intro')}</Text>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_setup_option_1')}</Text>
                                        </li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_setup_option_2')}</Text>
                                        </li>
                                        <ul style={{ listStyleType: 'circle', paddingLeft: '20px', margin: '0' }}>
                                            <li><Text
                                                color="color.text.primary">{t('passkey_help_section_2_setup_option_2a')}</Text>
                                            </li>
                                            <li><Text
                                                color="color.text.primary">{t('passkey_help_section_2_setup_option_2b')}</Text>
                                            </li>
                                        </ul>
                                    </ul>
                                </Col>

                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_section_2_process_title')}</Text>
                                    <Text color="color.text.primary">{t('passkey_help_section_2_process_intro')}</Text>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_process_step_1')}</Text>
                                        </li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_process_step_2')}</Text>
                                        </li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_process_step_3')}</Text>
                                        </li>
                                        <li><Text color="color.text.primary">{t('passkey_help_section_2_process_step_4')}</Text>
                                        </li>
                                    </ul>
                                </Col>
                            </Col>
                        </Expander>

                        {/* Section 3: Can I log in without a Passkey? */}
                        <Expander
                            isExpanded={isExpanded('login-without-passkey')}
                            onToggle={() => toggleSection('login-without-passkey')}
                            titleText={t('passkey_help_section_3_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({ titleText }) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="color.text.primary"
                                    style={{ cursor: 'pointer', height: '24px' }}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col id="login-without-passkey" gridGap="S400">
                                <Text color="color.text.primary">{t('passkey_help_section_3_content_1')}</Text>
                            </Col>
                        </Expander>

                        {/* Section 4: Passkeys device requirements */}
                        <Expander
                            isExpanded={isExpanded('device-requirements')}
                            onToggle={() => toggleSection('device-requirements')}
                            titleText={t('passkey_help_section_4_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({ titleText }) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="color.text.primary"
                                    style={{ cursor: 'pointer', height: '24px' }}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col id="device-requirements" gridGap="S400">
                                <Col gridGap="S300">
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_section_4_mobile_title')}</Text>
                                    <Row alignItems="center" gridGap="S200">
                                        <img src={iosDeviceIcon} alt="iOS" width="24" height="24" />
                                        <Text fontWeight="bold"
                                            color="color.text.primary">{t('passkey_help_section_4_ios_title')}</Text>
                                    </Row>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_ios_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_ios_requirement_2')}</Text>
                                        </li>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_ios_requirement_3')}</Text>
                                        </li>
                                    </ul>

                                    <Row alignItems="center" gridGap="S200">
                                        <img src={androidDeviceIcon} alt="Android" width="24" height="24" />
                                        <Text fontWeight="bold"
                                            color="color.text.primary">{t('passkey_help_section_4_android_title')}</Text>
                                    </Row>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_android_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_android_requirement_2')}</Text>
                                        </li>
                                    </ul>
                                </Col>

                                <Col gridGap="S300">
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_section_4_desktop_title')}</Text>
                                    <Row alignItems="center" gridGap="S200">
                                        <img src={macDeviceIcon} alt="Mac" width="24" height="24" />
                                        <Text fontWeight="bold"
                                            color="color.text.primary">{t('passkey_help_section_4_mac_title')}</Text>
                                    </Row>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_mac_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_mac_requirement_2')}</Text>
                                        </li>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_mac_requirement_3')}</Text>
                                        </li>
                                    </ul>

                                    <Row alignItems="center" gridGap="S200">
                                        <img src={windowsDeviceIcon} alt="Windows" width="24" height="24" />
                                        <Text fontWeight="bold"
                                            color="color.text.primary">{t('passkey_help_section_4_windows_title')}</Text>
                                    </Row>
                                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '0' }}>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_windows_requirement_1')}</Text>
                                        </li>
                                        <li><Text
                                            color="color.text.primary">{t('passkey_help_section_4_windows_requirement_2')}</Text>
                                        </li>
                                    </ul>
                                </Col>
                            </Col>
                        </Expander>

                        {/* Section 5: Updating contact information and on-site computer passwords */}
                        <Expander
                            isExpanded={isExpanded('contact-info')}
                            onToggle={() => toggleSection('contact-info')}
                            titleText={t('passkey_help_section_5_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({ titleText }) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="color.text.primary"
                                    style={{ cursor: 'pointer', height: '24px' }}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col id="contact-info" gridGap="S400">
                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_faq_question_5_1')}</Text>
                                    <Text color="color.text.primary">{t('passkey_help_faq_answer_5_1')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_faq_question_5_2')}</Text>
                                    <Text color="color.text.primary">
                                        {t('passkey_help_faq_answer_5_2_1')}
                                        {' '}
                                        <a
                                            href={"{{PassportV1BaseUrl}}/idp/reset/resetPassword"}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: token('color.link.primary-default'), textDecoration: 'underline' }}
                                        >
                                            {t('passkey_help_faq_answer_5_2_2')}
                                        </a>
                                        {' '}
                                        {t('passkey_help_faq_answer_5_2_3')}
                                    </Text>
                                </Col>
                            </Col>
                        </Expander>

                        {/* Section 6: Verifying email and phone number */}
                        <Expander
                            isExpanded={isExpanded('verify-email-phone')}
                            onToggle={() => toggleSection('verify-email-phone')}
                            titleText={t('passkey_help_section_6_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({ titleText }) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="color.text.primary"
                                    style={{ cursor: 'pointer', height: '24px' }}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col id="verify-email-phone" gridGap="S400">
                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_faq_question_6_1')}</Text>
                                    <Text color="color.text.primary">{t('passkey_help_faq_answer_6_1')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_faq_question_6_2')}</Text>
                                    <Text color="color.text.primary">{t('passkey_help_faq_answer_6_2')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_faq_question_6_3')}</Text>
                                    <Text color="color.text.primary">{t('passkey_help_faq_answer_6_3')}</Text>
                                </Col>
                                <Col>
                                    <Text fontWeight="bold"
                                        color="color.text.primary">{t('passkey_help_faq_question_6_4')}</Text>
                                    <Text color="color.text.primary">{t('passkey_help_faq_answer_6_4')}</Text>
                                </Col>
                            </Col>
                        </Expander>

                        {/* Section 7: Who can I contact for more help? */}
                        <Expander
                            isExpanded={isExpanded('help-contacts')}
                            onToggle={() => toggleSection('help-contacts')}
                            titleText={t('passkey_help_section_7_title')}
                            layout={ExpanderLayout.Card}
                            contentPadding={16}
                            renderTitle={({ titleText }) => (
                                <Text
                                    variant={TextVariant.BodyMDInteractive}
                                    color="color.text.primary"
                                    style={{ cursor: 'pointer', height: '24px' }}
                                >
                                    {titleText}
                                </Text>
                            )}
                        >
                            <Col id="help-contacts" gridGap="S400">
                                <Text color="color.text.primary">{t('passkey_help_section_7_content')}</Text>

                                {/* United States */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_united_states')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_united_states_phone'))}
                                    </Text>
                                </Col>

                                {/* United Kingdom */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_united_kingdom')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_united_kingdom_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_united_kingdom_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_united_kingdom_hours')}</Text>
                                    )}
                                </Col>

                                {/* Canada */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_canada')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_canada_phone'))}
                                    </Text>
                                </Col>

                                {/* Costa Rica */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_costa_rica')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_costa_rica_phone'))}
                                    </Text>
                                </Col>

                                {/* Czech Republic */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_czech_republic')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_czech_republic_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_czech_republic_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_czech_republic_hours')}</Text>
                                    )}
                                </Col>

                                {/* Ireland */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_ireland')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_ireland_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_ireland_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_ireland_hours')}</Text>
                                    )}
                                </Col>

                                {/* Poland */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_poland')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_poland_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_poland_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_poland_hours')}</Text>
                                    )}
                                </Col>

                                {/* South Africa */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_south_africa')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_south_africa_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_south_africa_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_south_africa_hours')}</Text>
                                    )}
                                </Col>

                                {/* India */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_india')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_india_phone'))}
                                    </Text>
                                    <Text color="color.text.primary">
                                        Email ERC at <a href={`mailto:${t('passkey_help_section_7_india_email')}`} style={{ color: token('color.link.primary-default'), textDecoration: 'underline' }}>
                                            {t('passkey_help_section_7_india_email')}
                                        </a>
                                    </Text>
                                </Col>

                                {/* Germany */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_germany')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_germany_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_germany_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_germany_hours')}</Text>
                                    )}
                                </Col>

                                {/* France */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_france')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_france_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_france_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_france_hours')}</Text>
                                    )}
                                </Col>

                                {/* Italy */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_italy')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_italy_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_italy_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_italy_hours')}</Text>
                                    )}
                                </Col>

                                {/* Slovakia */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_slovakia')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_slovakia_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_slovakia_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_slovakia_hours')}</Text>
                                    )}
                                </Col>

                                {/* Spain */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_spain')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_spain_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_spain_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_spain_hours')}</Text>
                                    )}
                                </Col>

                                {/* Australia */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_australia')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_australia_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_australia_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_australia_hours')}</Text>
                                    )}
                                </Col>

                                {/* Bahrain */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_bahrain')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_bahrain_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_bahrain_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_bahrain_hours')}</Text>
                                    )}
                                </Col>

                                {/* Belgium */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_belgium')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_belgium_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_belgium_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_belgium_hours')}</Text>
                                    )}
                                </Col>

                                {/* China */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_china')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_china_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_china_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_china_hours')}</Text>
                                    )}
                                </Col>

                                {/* Egypt */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_egypt')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_egypt_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_egypt_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_egypt_hours')}</Text>
                                    )}
                                </Col>

                                {/* Finland */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_finland')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_finland_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_finland_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_finland_hours')}</Text>
                                    )}
                                </Col>

                                {/* Japan */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_japan')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_japan_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_japan_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_japan_hours')}</Text>
                                    )}
                                </Col>

                                {/* Mexico */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_mexico')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_mexico_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_mexico_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_mexico_hours')}</Text>
                                    )}
                                </Col>

                                {/* Morocco */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_morocco')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_morocco_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_morocco_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_morocco_hours')}</Text>
                                    )}
                                </Col>

                                {/* Netherlands */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_netherlands')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_netherlands_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_netherlands_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_netherlands_hours')}</Text>
                                    )}
                                </Col>

                                {/* New Zealand */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_new_zealand')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_new_zealand_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_new_zealand_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_new_zealand_hours')}</Text>
                                    )}
                                </Col>

                                {/* Philippines */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_philippines')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_philippines_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_philippines_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_philippines_hours')}</Text>
                                    )}
                                </Col>

                                {/* Romania */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_romania')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_romania_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_romania_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_romania_hours')}</Text>
                                    )}
                                </Col>

                                {/* Singapore */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_singapore')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_singapore_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_singapore_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_singapore_hours')}</Text>
                                    )}
                                </Col>

                                {/* Sweden */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_sweden')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_sweden_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_sweden_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_sweden_hours')}</Text>
                                    )}
                                </Col>

                                {/* Switzerland */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_switzerland')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_switzerland_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_switzerland_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_switzerland_hours')}</Text>
                                    )}
                                </Col>

                                {/* UAE */}
                                <Col>
                                    <Text fontWeight="bold" color="color.text.primary">{t('passkey_help_section_7_uae')}</Text>
                                    <Text color="color.text.primary">
                                        Phone: {renderPhoneLinks(t('passkey_help_section_7_uae_phone'))}
                                    </Text>
                                    {t('passkey_help_section_7_uae_hours') && (
                                        <Text color="color.text.primary">{t('passkey_help_section_7_uae_hours')}</Text>
                                    )}
                                </Col>
                            </Col>
                        </Expander>
                    </Col>
                </View>
                <Footer />
            </Col>
        </>
    );
};

export default PasskeyHelp;
